import { service_open_pose } from '@/services/open_pose/service_open_pose';
import { assert_not_null } from '@shared/asserts/utils_asserts';
import * as THREE from 'three';
import { ConstantsOpenPose } from '../constants_open_pose';
import { EThreejsStub } from '../enums/e_threejs_stub';
import { get_material_first } from '../open_pose_body/open_pose_body_utils';
import { OpenPoseBodyKeypoints } from '../open_pose_types/open_pose_body_keypoints';

export class OpenPoseBodyProjectionController {
  private projection_billboard: THREE.Mesh;
  private projection_texture: THREE.Texture;
  private is_projection_updating: boolean;

  // creates once and reuses for drawing
  private canvas!: HTMLCanvasElement;
  private canvas_context!: CanvasRenderingContext2D;

  /*
   * METHODS
   */

  constructor() {
    this.projection_billboard = EThreejsStub.MESH;
    this.projection_texture = EThreejsStub.TEXTURE;

    this.is_projection_updating = false;

    // create invisible canvas once and attach to DOM for reuse across frames
    this.init_projection_canvas_once();
  }

  private get_body_key_points() {
    const out: OpenPoseBodyKeypoints = [];

    for (const node_name in service_open_pose.body_controller.nodes) {
      const node = service_open_pose.body_controller.nodes[node_name];

      const world_position = node.object.get_world_position_zero_offset();

      const width = service_open_pose.billboard_controller.width;
      const height = service_open_pose.billboard_controller.height;

      const { x, y } = world_position;

      const side_x = x / width + 0.5;
      const side_y = (y - height / 2) / height + 0.5;

      const screen_position = new THREE.Vector2(side_x, 1 - side_y);

      out.push({ name: node_name, screen_position });
    }

    return out;
  }

  private init_projection_canvas_once() {
    const canvas = document.createElement('canvas'); // keeps a single offscreen canvas for all renders
    const resolution = ConstantsOpenPose.RESOLUTION_FOR_BODY_PROJECTION;

    canvas.width = resolution;
    canvas.height = resolution;
    canvas.setAttribute('data-openpose-projection', 'true');

    document.body.appendChild(canvas);

    const canvas_context = canvas.getContext('2d');
    assert_not_null(canvas_context);

    this.canvas = canvas;
    this.canvas_context = canvas_context;
  }

  public remove_projection_canvas_from_dom() {
    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
  }

  private draw_connections_on_context(canvas: HTMLCanvasElement, canvas_context: CanvasRenderingContext2D, key_points: OpenPoseBodyKeypoints) {
    const point_map = new Map<string, THREE.Vector2>();
    key_points.forEach(point => point_map.set(point.name, point.screen_position));

    for (const [point_start, point_end] of ConstantsOpenPose.BODY_CONNECTIONS) {
      const start = point_map.get(point_start);
      const end = point_map.get(point_end);

      if (!start || !end) continue;

      const start_x = start.x * canvas.width;
      const start_y = start.y * canvas.height;
      const end_x = end.x * canvas.width;
      const end_y = end.y * canvas.height;

      // note: skip drawing segments if any endpoint falls outside the canvas to avoid unnecessary strokes and artifacts
      if (start_x < 0 || start_x > canvas.width || start_y < 0 || start_y > canvas.height) continue;
      // note: same check for the other endpoint; prevents partially offscreen lines from being rendered
      if (end_x < 0 || end_x > canvas.width || end_y < 0 || end_y > canvas.height) continue;

      canvas_context.strokeStyle = 'white';
      canvas_context.lineWidth = 6;
      canvas_context.beginPath();
      canvas_context.moveTo(start_x, start_y);
      canvas_context.lineTo(end_x, end_y);
      canvas_context.stroke();

      canvas_context.strokeStyle = 'black';
      canvas_context.lineWidth = 3;
      canvas_context.beginPath();
      canvas_context.moveTo(start_x, start_y);
      canvas_context.lineTo(end_x, end_y);
      canvas_context.stroke();
    }
  }

  private draw_points_on_context(canvas: HTMLCanvasElement, canvas_context: CanvasRenderingContext2D, key_points: OpenPoseBodyKeypoints) {
    for (const point of key_points) {
      const x = point.screen_position.x * canvas.width;
      const y = point.screen_position.y * canvas.height;

      // note: skip dots outside the canvas to prevent needless path calls and save fill operations
      if (x < 0 || x > canvas.width || y < 0 || y > canvas.height) continue;

      canvas_context.fillStyle = 'white';
      canvas_context.beginPath();
      canvas_context.arc(x, y, 2.5, 0, Math.PI * 2);
      canvas_context.fill();

      canvas_context.fillStyle = 'black';
      canvas_context.beginPath();
      canvas_context.arc(x, y, 1.5, 0, Math.PI * 2);
      canvas_context.fill();
    }
  }

  private create_body_projection() {
    const key_points = this.get_body_key_points();
    if (key_points.length === 0) return EThreejsStub.CANVAS_TEXTURE;

    // reuse single canvas/context created at init
    const canvas = this.canvas;
    const canvas_context = this.canvas_context;

    canvas_context.clearRect(0, 0, canvas.width, canvas.height);

    this.draw_connections_on_context(canvas, canvas_context, key_points);
    this.draw_points_on_context(canvas, canvas_context, key_points);

    const canvas_texture = new THREE.CanvasTexture(canvas);
    canvas_texture.needsUpdate = true;
    canvas_texture.magFilter = THREE.LinearFilter;
    canvas_texture.minFilter = THREE.LinearFilter;

    return canvas_texture;
  }

  private create_or_update_projection_billboard(projection_texture: THREE.Texture) {
    if (this.projection_billboard !== EThreejsStub.MESH) {
      service_open_pose.scene.remove(this.projection_billboard);

      this.projection_billboard.geometry.dispose(); //todo: test 2nd dispose

      const material_old = get_material_first(this.projection_billboard);

      material_old.dispose();
    }

    const width = service_open_pose.billboard_controller.width;
    const height = service_open_pose.billboard_controller.height;

    const plane_geometry = new THREE.PlaneGeometry(width, height);
    const meterial = new THREE.MeshBasicMaterial({ map: projection_texture, transparent: true, side: THREE.DoubleSide, alphaTest: 0.01 });

    const mesh = new THREE.Mesh(plane_geometry, meterial);
    mesh.position.set(0, height / 2, ConstantsOpenPose.PROJECTION_BILLBOARD_POSITION_Y);
    mesh.name = ConstantsOpenPose.PROJECTION_BILLBOARD_NAME;

    this.projection_billboard = mesh;

    service_open_pose.scene.add(mesh);
  }

  //todo: test trottle or debounce for remove is_projection_updating
  public update_body_projection() {
    if (this.is_projection_updating) return;

    this.is_projection_updating = true;

    const body_projection_texture = this.create_body_projection();

    if (!body_projection_texture) {
      this.is_projection_updating = false;
      return;
    }

    if (this.projection_texture) this.projection_texture.dispose();
    this.projection_texture = body_projection_texture;

    this.create_or_update_projection_billboard(body_projection_texture);
    this.is_projection_updating = false;
  }

  public dispose_projection() {
    this.projection_texture.dispose();
    this.projection_texture = new THREE.Texture();

    if (this.projection_billboard) {
      service_open_pose.scene.remove(this.projection_billboard);

      this.projection_billboard.geometry.dispose();

      const material = this.projection_billboard.material;

      if (material instanceof THREE.Material) material.dispose();
    }

    // remove the single canvas from DOM when disposing the controller
    this.remove_projection_canvas_from_dom();
  }
}
